import { useState, useRef, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { ChevronDown, X, RefreshCw, Plus } from "lucide-react";

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  onRefresh?: () => void;
  onCreateNew?: () => void;
  createLabel?: string;
}

export function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Select...",
  onRefresh,
  onCreateNew,
  createLabel = "Create new",
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const filtered = options.filter(
    (o) => !selected.includes(o.value) && o.label.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        className={cn(
          "flex items-center gap-1 min-h-[36px] px-2 py-1 rounded-sm border border-input bg-card cursor-pointer transition-[border-color,box-shadow] duration-150 ease-out hover:border-border-hover",
          open && "border-ring ring-[3px] ring-ring/30"
        )}
        onClick={() => setOpen(!open)}
      >
        <div className="flex flex-wrap gap-1 flex-1">
          {selected.length === 0 ? (
            <span className="text-sm text-muted-foreground">{placeholder}</span>
          ) : (
            selected.map((val) => {
              const opt = options.find((o) => o.value === val);
              return (
                <Badge key={val} variant="secondary" className="gap-1 pr-0.5 text-xs">
                  {opt?.label ?? val}
                  <button
                    type="button"
                    aria-label={`Remove ${opt?.label ?? val}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(val);
                    }}
                    className="cursor-pointer hover:text-destructive-text transition-colors duration-150"
                  >
                    <X size={10} />
                  </button>
                </Badge>
              );
            })
          )}
        </div>
        <div className="flex items-center gap-1">
          {onRefresh && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRefresh();
              }}
              className="p-0.5 hover:text-accent-foreground rounded"
              title="Refresh options"
            >
              <RefreshCw size={12} />
            </button>
          )}
          <ChevronDown size={14} className="text-muted-foreground shrink-0" />
        </div>
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-lg bg-popover shadow-menu max-h-60 overflow-y-auto scrollbar-thin">
          <div className="p-1.5 border-b border-border">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="h-7 text-xs"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">No matches</div>
          )}
          {filtered.map((opt) => (
            <div
              key={opt.value}
              className="px-3 py-1.5 text-sm cursor-pointer hover:bg-muted flex items-center gap-2"
              onClick={() => toggle(opt.value)}
            >
              <div
                className={cn(
                  "w-3.5 h-3.5 rounded border border-muted-foreground/50 flex items-center justify-center",
                  selected.includes(opt.value) && "bg-primary border-primary"
                )}
              >
                {selected.includes(opt.value) && (
                  <svg width="10" height="10" viewBox="0 0 10 10"><path d="M2 5l2 2 4-4" stroke="white" strokeWidth="1.5" fill="none" /></svg>
                )}
              </div>
              {opt.label}
            </div>
          ))}
          {onCreateNew && (
            <>
              <div className="border-t border-border" />
              <button
                type="button"
                className="w-full px-3 py-1.5 text-sm text-left text-accent-foreground hover:bg-muted flex items-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  onCreateNew();
                }}
              >
                <Plus size={12} />
                {createLabel}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
